import { useQuery } from '@tanstack/react-query';
import {
  getCategories,
  getSpecialties,
  getExpertise,
  getSpecialtiesGroupedByCategory,
  searchCategories,
  searchSpecialties,
  searchExpertise,
} from '../api/taxonomyApi';
import { useMemo } from 'react';

/**
 * Query key factory for taxonomy queries
 * 
 * Implements hierarchical query keys for granular cache invalidation:
 * - all: invalidate all taxonomy data
 * - categories: invalidate category lists
 * - specialties: invalidate specialty lists (with optional categoryId filter)
 * - expertise: invalidate expertise lists
 */
export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  categories: () => [...taxonomyKeys.all, 'categories'] as const,
  specialties: () => [...taxonomyKeys.all, 'specialties'] as const,
  specialtiesByCategory: (categoryId?: string) =>
    [...taxonomyKeys.specialties(), categoryId] as const,
  expertise: () => [...taxonomyKeys.all, 'expertise'] as const,
};

/**
 * Hook to fetch all active categories
 * 
 * Categories represent top-level provider classifications such as:
 * - Home Healthcare
 * - Legal Services
 * - Financial Services
 * - Memory Care
 * 
 * Results are sorted by displayOrder for consistent presentation.
 * Data is cached for 30 minutes since taxonomy changes infrequently.
 * 
 * @returns Categories query with helper functions
 */
export const useCategories = () => {
  const query = useQuery({
    queryKey: taxonomyKeys.categories(),
    queryFn: getCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes - taxonomy data rarely changes
  });

  /**
   * Search categories by name (case-insensitive)
   */
  const search = useMemo(
    () => (searchTerm: string) => {
      if (!query.data?.categories) return [];
      return searchCategories(query.data.categories, searchTerm);
    },
    [query.data?.categories]
  );

  /**
   * Get category by ID
   */
  const getById = useMemo(
    () => (id: string) => {
      return query.data?.categories.find((cat) => cat.id === id);
    },
    [query.data?.categories]
  );

  return {
    categories: query.data?.categories || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    search,
    getById,
  };
};

/**
 * Hook to fetch all active specialties with optional category filtering
 * 
 * Specialties represent specific professional roles such as:
 * - Geriatric Care Manager
 * - Elder Law Attorney
 * - Hospice Nurse
 * - Estate Planning Attorney
 * 
 * Can be filtered by categoryId to show specialties for a specific category.
 * Results sorted by displayOrder within each category.
 * 
 * Note: Large dataset (50-200+ items) - use virtual scrolling in UI.
 * Data is cached for 30 minutes since taxonomy changes infrequently.
 * 
 * @param categoryId - Optional category ID to filter specialties
 * @returns Specialties query with helper functions
 */
export const useSpecialties = (categoryId?: string) => {
  const query = useQuery({
    queryKey: taxonomyKeys.specialtiesByCategory(categoryId),
    queryFn: () => getSpecialties(categoryId),
    staleTime: 30 * 60 * 1000, // 30 minutes - taxonomy data rarely changes
  });

  /**
   * Search specialties by name (case-insensitive)
   */
  const search = useMemo(
    () => (searchTerm: string) => {
      if (!query.data?.specialties) return [];
      return searchSpecialties(query.data.specialties, searchTerm);
    },
    [query.data?.specialties]
  );

  /**
   * Get specialty by ID
   */
  const getById = useMemo(
    () => (id: string) => {
      return query.data?.specialties.find((spec) => spec.id === id);
    },
    [query.data?.specialties]
  );

  /**
   * Filter specialties by category ID
   */
  const filterByCategory = useMemo(
    () => (catId: string) => {
      if (!query.data?.specialties) return [];
      return query.data.specialties.filter(
        (spec) => spec.categoryId === catId
      );
    },
    [query.data?.specialties]
  );

  return {
    specialties: query.data?.specialties || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    search,
    getById,
    filterByCategory,
  };
};

/**
 * Hook to fetch specialties grouped by category
 * 
 * Returns specialties organized by their parent category.
 * Useful for rendering grouped/hierarchical dropdowns in the UI.
 * 
 * Data is cached for 30 minutes since taxonomy changes infrequently.
 * 
 * @returns Grouped specialties query
 */
export const useSpecialtiesGrouped = () => {
  const query = useQuery({
    queryKey: [...taxonomyKeys.specialties(), 'grouped'],
    queryFn: getSpecialtiesGroupedByCategory,
    staleTime: 30 * 60 * 1000, // 30 minutes - taxonomy data rarely changes
  });

  /**
   * Get all specialties for a specific category
   */
  const getSpecialtiesByCategory = useMemo(
    () => (categoryId: string) => {
      if (!query.data) return [];
      const group = query.data.find(
        (g) => g.category?.id === categoryId
      );
      return group?.specialties || [];
    },
    [query.data]
  );

  return {
    groups: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    getSpecialtiesByCategory,
  };
};

/**
 * Hook to fetch all active expertise areas
 * 
 * Expertise represents specific skills or knowledge areas such as:
 * - Dementia Care
 * - Estate Planning
 * - Medicare Navigation
 * - Family Counseling
 * 
 * Results sorted by displayOrder for consistent presentation.
 * 
 * Note: Large dataset (50-200+ items) - use virtual scrolling in UI.
 * Data is cached for 30 minutes since taxonomy changes infrequently.
 * 
 * @returns Expertise query with helper functions
 */
export const useExpertise = () => {
  const query = useQuery({
    queryKey: taxonomyKeys.expertise(),
    queryFn: getExpertise,
    staleTime: 30 * 60 * 1000, // 30 minutes - taxonomy data rarely changes
  });

  /**
   * Search expertise by name (case-insensitive)
   */
  const search = useMemo(
    () => (searchTerm: string) => {
      if (!query.data?.expertise) return [];
      return searchExpertise(query.data.expertise, searchTerm);
    },
    [query.data?.expertise]
  );

  /**
   * Get expertise by ID
   */
  const getById = useMemo(
    () => (id: string) => {
      return query.data?.expertise.find((exp) => exp.id === id);
    },
    [query.data?.expertise]
  );

  return {
    expertise: query.data?.expertise || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    search,
    getById,
  };
};

/**
 * Hook to fetch all taxonomy data at once
 * 
 * Useful for pages that need all taxonomy lists (e.g., advanced search filters).
 * Executes all queries in parallel for optimal performance.
 * 
 * Data is cached for 30 minutes since taxonomy changes infrequently.
 * 
 * @returns Combined taxonomy data from all queries
 */
export const useAllTaxonomy = () => {
  const categoriesQuery = useCategories();
  const specialtiesQuery = useSpecialties();
  const expertiseQuery = useExpertise();

  const isLoading =
    categoriesQuery.isLoading ||
    specialtiesQuery.isLoading ||
    expertiseQuery.isLoading;

  const isError =
    categoriesQuery.isError ||
    specialtiesQuery.isError ||
    expertiseQuery.isError;

  return {
    categories: categoriesQuery.categories,
    specialties: specialtiesQuery.specialties,
    expertise: expertiseQuery.expertise,
    isLoading,
    isError,
    categoriesQuery,
    specialtiesQuery,
    expertiseQuery,
  };
};

/**
 * Hook to get taxonomy item names by IDs
 * 
 * Helper hook for displaying selected filter tags.
 * Looks up names from cached taxonomy data.
 * 
 * @param selectedIds - Selected taxonomy IDs
 * @returns Object with lookup functions
 */
export const useTaxonomyLookup = () => {
  const { categories } = useCategories();
  const { specialties } = useSpecialties();
  const { expertise } = useExpertise();

  /**
   * Get category names by IDs
   */
  const getCategoryNames = useMemo(
    () => (ids: string[]) => {
      return ids
        .map((id) => categories.find((cat) => cat.id === id)?.name)
        .filter(Boolean) as string[];
    },
    [categories]
  );

  /**
   * Get specialty names by IDs
   */
  const getSpecialtyNames = useMemo(
    () => (ids: string[]) => {
      return ids
        .map((id) => specialties.find((spec) => spec.id === id)?.name)
        .filter(Boolean) as string[];
    },
    [specialties]
  );

  /**
   * Get expertise names by IDs
   */
  const getExpertiseNames = useMemo(
    () => (ids: string[]) => {
      return ids
        .map((id) => expertise.find((exp) => exp.id === id)?.name)
        .filter(Boolean) as string[];
    },
    [expertise]
  );

  return {
    getCategoryNames,
    getSpecialtyNames,
    getExpertiseNames,
  };
};

